import { Box } from '@mui/material';
import React, { useEffect, useState, ReactNode } from 'react';

interface ContentMinHeightProps {
  children: ReactNode;
}

/**
 * Component that wraps content and ensures it has a minimum height to push
 * the footer to the bottom of the viewport when content is sparse.
 *
 * Dynamically calculates height based on:
 * - Main header: #bright-megamenu (non-SaaS) OR [data-app-header] (SaaS/custom)
 * - Listing header: [data-listing-header] (EditHeader, if present)
 * - Footer: [data-app-footer]
 *
 * Handles variable heights and visibility changes (e.g., mega menu hiding on scroll).
 */
export const ContentMinHeight: React.FC<ContentMinHeightProps> = ({
  children,
}) => {
  const [minHeight, setMinHeight] = useState<string>('auto');

  useEffect(() => {
    const calculateMinHeight = () => {
      let totalHeight = 0;

      // Check for main header (mega menu takes precedence)
      const megaMenu = document.getElementById('bright-megamenu');
      const appHeader = document.querySelector('[data-app-header]');
      const mainHeader = megaMenu || appHeader;

      if (mainHeader) {
        const rect = mainHeader.getBoundingClientRect();
        // Only count height if element is visible (not hidden via display:none or visibility:hidden)
        const styles = window.getComputedStyle(mainHeader);
        if (styles.display !== 'none' && styles.visibility !== 'hidden') {
          // Use offsetHeight to get actual rendered height (accounts for transforms)
          totalHeight +=
            (mainHeader as HTMLElement).offsetHeight || rect.height;
        }
      }

      // Check for listing header
      const listingHeader = document.querySelector('[data-listing-header]');
      if (listingHeader) {
        const rect = listingHeader.getBoundingClientRect();
        const styles = window.getComputedStyle(listingHeader);
        if (styles.display !== 'none' && styles.visibility !== 'hidden') {
          totalHeight +=
            (listingHeader as HTMLElement).offsetHeight || rect.height;
        }
      }

      // Check for footer
      const footer = document.querySelector('[data-app-footer]');
      if (footer) {
        const rect = footer.getBoundingClientRect();
        const styles = window.getComputedStyle(footer);
        if (styles.display !== 'none' && styles.visibility !== 'hidden') {
          totalHeight += (footer as HTMLElement).offsetHeight || rect.height;
        }
      }

      // Set minHeight to fill remaining viewport
      const calculatedMinHeight = `calc(100vh - ${totalHeight}px)`;
      setMinHeight(calculatedMinHeight);
    };

    // Initial calculation
    calculateMinHeight();

    // Set up ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateMinHeight();
    });

    // Observe all relevant elements
    const megaMenu = document.getElementById('bright-megamenu');
    const appHeader = document.querySelector('[data-app-header]');
    const listingHeader = document.querySelector('[data-listing-header]');
    const footer = document.querySelector('[data-app-footer]');

    if (megaMenu) resizeObserver.observe(megaMenu);
    if (appHeader) resizeObserver.observe(appHeader as Element);
    if (listingHeader) resizeObserver.observe(listingHeader);
    if (footer) resizeObserver.observe(footer);

    // Also recalculate on scroll (for mega menu hide/show behavior)
    const handleScroll = () => {
      calculateMinHeight();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Recalculate on window resize
    const handleResize = () => {
      calculateMinHeight();
    };
    window.addEventListener('resize', handleResize);

    // Set up MutationObserver to watch for DOM changes (mega menu injection)
    const mutationObserver = new MutationObserver(() => {
      calculateMinHeight();

      // Re-observe if mega menu was just added
      const newMegaMenu = document.getElementById('bright-megamenu');
      if (newMegaMenu && !megaMenu) {
        resizeObserver.observe(newMegaMenu);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );
};
